/**
 * The form's own checks, in plain words that say what to do (section 4): run on a field when the
 * editor leaves it, and on every field when they save, where the errors also make the summary at
 * the top. Errors stop a save; warnings only inform. The server checks every save against the
 * feed schema as well, so these are for clarity, not for safety.
 *
 * `prepareForSave` fills in what the feed requires but nobody should have to think about (an
 * empty artwork set, empty ingredient lists, "free" when nothing was chosen). Pure, unit-tested.
 */

import type { EntityType } from "../api/admin";

type Doc = Record<string, unknown>;

/** The value at a dot path (no runtime imports here, so the unit tests run this file as it is). */
function getPath(doc: unknown, path: string): unknown {
  let at: unknown = doc;
  for (const part of path.split(".")) {
    if (at === null || typeof at !== "object") return undefined;
    at = (at as Record<string, unknown>)[part];
  }
  return at;
}

export interface Issue {
  path: string;
  message: string;
  level: "error" | "warning";
}

export interface CheckContext {
  /** What the save makes it: shown in the app ("ready") needs a complete item. */
  status: "draft" | "ready" | "archived";
  locale: string;
  /** The attached video's state, when it is one of the brand's uploads. */
  video?: "ready" | "processing" | "failed" | "missing" | null;
  /** The video's length in seconds, when known (moments after it are flagged). */
  duration?: number;
}

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const localized = (v: unknown, locale: string) => text((v as Record<string, string> | undefined)?.[locale]) || Object.values((v as Record<string, string> | undefined) ?? {}).find((s) => typeof s === "string" && s.trim()) || "";
const time = (v: unknown) => (typeof v === "string" ? Date.parse(v) : NaN);
const https = (v: unknown) => typeof v === "string" && /^https:\/\/[^\s/]+\.[^\s]+/.test(v.trim());
const list = (v: unknown) => (Array.isArray(v) ? (v as Doc[]) : []);

export function checkItem(type: EntityType, doc: Doc, ctx: CheckContext): Issue[] {
  const out: Issue[] = [];
  const error = (path: string, message: string) => out.push({ path, message, level: "error" });
  const warn = (path: string, message: string) => out.push({ path, message, level: "warning" });
  const showing = ctx.status === "ready";

  // The name.
  if (type === "person") { if (!text(doc["name"])) error("name", "Give them a name."); }
  else if (!localized(doc["title"], ctx.locale)) error("title", "Give it a name.");

  // A title's video.
  if (type === "title") {
    const video = getPath(doc, "playback.video.videoId");
    if (!video) {
      if (showing) error("playback.video.videoId", "Add a video before you show it in the app. You can keep it as a draft without one.");
    } else if (ctx.video === "failed") {
      error("playback.video.videoId", "The video did not encode. Upload it again or choose another one.");
    } else if (ctx.video === "missing") {
      // Imported catalogues name videos stored before this brand's Files existed: worth a look, not a blocker.
      warn("playback.video.videoId", "This video is not in Files. If it plays in the app, leave it; otherwise upload it again.");
    } else if (!doc["durationSeconds"] && showing) {
      error("playback.video.videoId", ctx.video === "processing" ? "The video is not ready yet: it is waiting for the encoder or being encoded. Keep it as a draft until it is ready." : "The video's length is unknown. Choose the video again so its length is read from it.");
    }
  }

  // A live event's times.
  if (type === "liveEvent") {
    const starts = time(doc["startsAt"]);
    if (Number.isNaN(starts)) error("startsAt", "Choose when it starts.");
    const ends = time(doc["endsAt"]);
    if (!Number.isNaN(ends) && !Number.isNaN(starts) && ends <= starts) error("endsAt", "The end must be after the start.");
    const doors = time(doc["doorsOpenAt"]);
    if (!Number.isNaN(doors) && !Number.isNaN(starts) && doors > starts) warn("doorsOpenAt", "The pre-show usually opens before the start.");
  }

  // When and where it shows.
  const from = time(getPath(doc, "availability.from"));
  const until = time(getPath(doc, "availability.until"));
  if (!Number.isNaN(from) && !Number.isNaN(until) && until <= from) error("availability.until", "The end must be after the start.");
  list(getPath(doc, "availability.countries")).forEach((c, i) => { if (!/^[A-Z]{2}$/.test(String(c))) error(`availability.countries.${i}`, `“${String(c)}” is not a country code. Use two letters, e.g. NL or BE.`); });

  // Who can watch.
  const ent = doc["entitlement"] as { access?: string; products?: string[] } | undefined;
  if (ent && ent.access && ent.access !== "free" && !ent.products?.length) error("entitlement", "Choose what unlocks it, or make it free for everyone.");

  // Recipe.
  const recipe = doc["recipe"] as Doc | undefined;
  if (recipe) {
    const servings = recipe["servings"];
    if (servings === undefined || servings === null) error("recipe.servings", "Say how many servings the recipe makes.");
    else if (!Number.isInteger(servings) || (servings as number) < 1) error("recipe.servings", "Servings is a whole number, 1 or more.");
    list(recipe["ingredients"]).forEach((ing, i) => {
      if (!localized(ing["name"], ctx.locale)) error(`recipe.ingredients.${i}.name`, "Name the ingredient.");
      if (ing["quantity"] !== undefined && !((ing["quantity"] as number) > 0)) error(`recipe.ingredients.${i}.quantity`, "The amount is more than 0, or leave it empty.");
    });
    list(recipe["steps"]).forEach((step, i) => { if (!localized(step["text"], ctx.locale)) error(`recipe.steps.${i}.text`, "Write what to do in this step."); });
  }

  // Moments on a video or a live event.
  for (const path of type === "liveEvent" ? ["timeline"] : type === "title" ? ["playback.markers"] : []) {
    list(getPath(doc, path)).forEach((m, i) => {
      const start = m["startSeconds"] as number, end = m["endSeconds"] as number | undefined;
      if (end !== undefined && end <= start) error(`${path}.${i}.endSeconds`, "The end must be after the start.");
      if (ctx.duration && start > ctx.duration) warn(`${path}.${i}.startSeconds`, "This is after the end of the video.");
    });
  }

  // Credits, links, licence credit, age rating.
  list(doc["credits"]).forEach((c, i) => {
    if (!c["personId"]) error(`credits.${i}.personId`, "Choose a person.");
    if (!text(c["role"])) error(`credits.${i}.role`, "Say what they did, e.g. director.");
  });
  list(doc["links"]).forEach((l, i) => {
    if (!localized(l["title"], ctx.locale)) error(`links.${i}.title`, "Give the link a label.");
    if (!https(l["url"])) error(`links.${i}.url`, "A link starts with https://, e.g. https://example.com/give");
  });
  const attribution = doc["attribution"] as Doc | undefined;
  if (attribution) {
    if (!text(attribution["text"])) error("attribution.text", "Write the credit line, or remove the licence credit.");
    if (attribution["url"] !== undefined && !https(attribution["url"])) error("attribution.url", "A link starts with https://");
  }
  const rating = doc["rating"] as Doc | undefined;
  if (rating && (!text(rating["system"]) || !text(rating["value"]))) error("rating", "Choose the rating system and the rating, or leave both empty.");

  // Lists inside a category list, collections, screens.
  if (type === "taxonomy") list(doc["terms"]).forEach((t, i) => { if (!localized(t["title"], ctx.locale)) error(`terms.${i}.title`, "Name it."); });
  if (type === "page") {
    list(doc["rails"]).forEach((r, i) => {
      if (r["kind"] === "collection" && !r["collectionId"]) error(`rails.${i}.collectionId`, "Choose the collection this row shows.");
      if (r["kind"] === "collections" && !list(r["collectionIds"]).length) error(`rails.${i}.collectionIds`, "Choose at least one collection.");
      if (r["kind"] === "term" && !r["termId"]) error(`rails.${i}.termId`, "Choose what this row shows.");
    });
  }
  // Placeholder text ("[poster 1200x1800]", "[credit line]"), as the publish check finds it.
  const walk = (v: unknown, path: string) => {
    if (typeof v === "string") { if (/^\[[^\]]+\]$/.test(v.trim())) error(fieldOfText(path, ctx.locale), "This is placeholder text. Replace it with the real thing."); return; }
    if (Array.isArray(v)) v.forEach((x, i) => walk(x, `${path}.${i}`));
    else if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) walk(x, path ? `${path}.${k}` : k);
  };
  walk(doc, "");
  return out;
}

/** Text in a language ("synopsis.en") is reported on its field ("synopsis"). */
function fieldOfText(path: string, locale: string): string {
  const last = path.slice(path.lastIndexOf(".") + 1);
  return path.includes(".") && (last === locale || (/^[a-z]{2}(-[A-Z]{2})?$/.test(last) && last !== "id")) ? path.slice(0, path.lastIndexOf(".")) : path;
}

/** The issues for one field and everything under it (a field shows its own on blur). */
export function issuesAt(issues: Issue[], path: string): Issue[] {
  return issues.filter((i) => i.path === path || i.path.startsWith(`${path}.`));
}

/**
 * The document as it is saved: the parts the feed requires but an editor never fills in by hand.
 * Nothing the editor wrote is changed.
 */
export function prepareForSave(type: EntityType, doc: Doc): Doc {
  const out: Doc = { ...doc };
  if (type === "title" || type === "series" || type === "liveEvent") {
    out["artwork"] = out["artwork"] ?? {};
    out["entitlement"] = out["entitlement"] ?? { access: "free" };
  }
  if (type === "series") out["seasons"] = list(out["seasons"]).map((s) => ({ ...s, titleIds: list(s["titleIds"]) }));
  if (type === "collection") out["itemIds"] = out["itemIds"] ?? [];
  if (type === "page") out["rails"] = out["rails"] ?? [];
  if (type === "taxonomy") out["terms"] = out["terms"] ?? [];
  // Moments and captions live with the video: without one there is no playback to save.
  if (type === "title" && out["playback"] && !getPath(out, "playback.video.videoId")) delete out["playback"];
  if (type === "title" && out["recipe"]) {
    const recipe = { ...(out["recipe"] as Doc) };
    recipe["ingredients"] = recipe["ingredients"] ?? [];
    recipe["steps"] = recipe["steps"] ?? [];
    const empty = recipe["servings"] === undefined && !list(recipe["ingredients"]).length && !list(recipe["steps"]).length && recipe["prepMinutes"] === undefined && recipe["cookMinutes"] === undefined;
    if (empty) delete out["recipe"]; else out["recipe"] = recipe;
  }
  return out;
}

/**
 * A save the server refused names "path: message" lines (valibot's dot paths). Back to issues, so
 * they open their fields like the form's own.
 */
export function serverIssues(lines: string[]): Issue[] {
  return lines.map((line) => {
    const m = line.match(/^([A-Za-z0-9_.]+|\(entity\)): (.*)$/);
    const path = m && m[1] !== "(entity)" ? m[1]! : "";
    const message = m ? m[2]! : line;
    return { path, level: "error" as const, message: plainSchemaMessage(path, message) };
  });
}

function plainSchemaMessage(path: string, message: string): string {
  if (/URLs must be https|not a URL/.test(message)) return "A link starts with https://";
  if (/^Invalid type: Expected Object but received undefined/.test(message) || /Invalid key/.test(message)) return `Something required is missing here (${path}).`;
  if (/placeholder/.test(message)) return "This is placeholder text. Replace it with the real thing.";
  return message[0]!.toUpperCase() + message.slice(1);
}
